import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import configData from '../../configure';
import './FeeBulkImport.css';
import { useAuth } from "../../AuthContext";
import { Loader2 } from 'lucide-react'; 
import { toast } from 'react-toastify';

// UUID generator
const generateUUID = () =>
    'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });

function FeeBulkImport() {
    const [file, setFile] = useState(null);
    const [uploadStatus, setUploadStatus] = useState('idle');
    const [importSummary, setImportSummary] = useState(null);
    const [classMap, setClassMap] = useState({}); // Name -> ID
    const [loadingClasses, setLoadingClasses] = useState(true);

    const navigate = useNavigate();
    const location = useLocation(); // Import useLocation
    const { axios, token, user, orgId, parentOrgId } = useAuth();
    const effectiveParentOrgId = parentOrgId || user?.parentOrgId || user?.orgDetails?.parentOrgId || '';
    const isSmallOrg = effectiveParentOrgId === 'SMALLORG-001';
    const { selectedClass } = location.state || {}; // Get passed classId

    // Fetch classes to map Class Name -> Class ID
    useEffect(() => {
        const fetchClasses = async () => {
            if (!orgId) return;
            try {
                const { data } = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`);
                const map = {};
                data.forEach(cls => {
                    const fullName = `${cls.className} ${cls.sectionName}`.trim();
                    map[fullName.toLowerCase()] = cls.classId;
                    // Also map just "Class X" if unique? Better strict matching.
                    // Let's stick to strict "ClassName SectionName" matching for now.
                });
                setClassMap(map);
            } catch (err) {
                console.error("Failed to load classes", err);
                toast.error("Failed to load class list. Import might fail.");
            } finally {
                setLoadingClasses(false);
            }
        };
        fetchClasses();
    }, [orgId, axios]);

    const handleFileChange = (event) => {
        const selectedFile = event.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== 'text/csv' && !selectedFile.name.toLowerCase().endsWith('.csv')) {
                toast.error('Please upload a valid CSV file.');
                setUploadStatus('error');
                return;
            }
            setFile(selectedFile);
            setUploadStatus('idle');
            setImportSummary(null);
        }
    };

    const parseDate = (dateStr) => {
        // Expecting YYYY-MM-DD
        const d = new Date(dateStr);
        return isNaN(d.getTime()) ? null : d.toISOString();
    };

    const handleUpload = async () => {
        if (!file || !orgId) return;

        setUploadStatus('processing');
        setImportSummary(null);

        const fileReader = new FileReader();
        fileReader.onload = async (e) => {
            const csvText = e.target.result;
            const rows = csvText.split('\n').map(r => r.trim()).filter(r => r);
            // Header: ClassName,AcademicYear,TermName,StartDate,EndDate,DueDate,AdmissionFee,TuitionFee,...

            const headers = rows[0].split(',').map(h => h.trim());
            const dataRows = rows.slice(1);

            // Group by Class + Year
            const groupedData = {}; // "classId|year" -> { classId, year, terms: [] }

            const stats = {
                totalClasses: 0,
                success: 0,
                duplicates: 0,
                failed: 0,
                errors: []
            };

            // 1. Parse and Group
            for (let i = 0; i < dataRows.length; i++) {
                const row = dataRows[i];
                const cols = row.split(',').map(c => c.trim());

                // Basic column mapping (Update indices based on template)
                // 0: AcademicYear, 1: TermName, 2: StartDate, 3: EndDate, 4: DueDate
                // 5+: Fee Components...

                if (cols.length < 5) continue;

                const academicYear = cols[0];
                const termName = cols[1];
                const startDate = parseDate(cols[2]);
                const endDate = parseDate(cols[3]);
                const dueDate = parseDate(cols[4]);

                // Use selectedClass passed from parent
                const classId = selectedClass;
                // Get class name from map (inverted lookup or just find)
                // Since classMap is Name->ID, we need ID->Name
                const className = Object.keys(classMap).find(key => classMap[key] === classId) || "Unknown Class";

                if (!startDate || !endDate || !dueDate) {
                    stats.errors.push({ row: i + 2, reason: `Invalid date(s) for "${termName}"` });
                    continue;
                }

                const key = `${classId}|${academicYear}`;
                if (!groupedData[key]) {
                    groupedData[key] = {
                        classId,
                        academicYear,
                        className, // for reporting
                        terms: []
                    };
                }

                // Fee Breakdown
                const feeBreakdown = {};
                // Iterate starting from col 5
                for (let j = 5; j < headers.length; j++) {
                    const feeName = headers[j];
                    const amount = parseFloat(cols[j]);
                    if (feeName && !isNaN(amount) && amount > 0) {
                        feeBreakdown[feeName] = amount;
                    }
                }

                groupedData[key].terms.push({
                    description: termName,
                    startDate,
                    endDate,
                    dueDate,
                    feeBreakdown
                });
            }

            stats.totalClasses = Object.keys(groupedData).length;

            // 2. Process Groups
            for (const key in groupedData) {
                const item = groupedData[key];

                try {
                    // Check for duplicate/existing
                    const existingRes = await axios.get(
                        `${configData.GET_FEE_STRUCTURE}/class/${item.classId}?academicYear=${item.academicYear}`,
                        { headers: { Authorization: `Bearer ${token}` } }
                    );
                    const existing = existingRes.data.find(f => f.academicYear === item.academicYear);

                    if (existing) {
                        stats.duplicates++;
                        stats.errors.push({
                            row: "N/A",
                            reason: `Fee structure already exists for ${item.className} (${item.academicYear}). Skipped.`
                        });
                        continue;
                    }

                    // Create Payload
                    const feePlanId = generateUUID();
                    const now = new Date().toISOString();
                    const createdBy = user?.email || "admin";

                    const payload = {
                        createdBy,
                        createdAt: now,
                        lastUpdatedBy: createdBy,
                        lastUpdatedAt: now,
                        feePlanId,
                        academicYear: item.academicYear,
                        active: true,
                        classId: item.classId,
                        description: item.className,
                        feeInstallments: item.terms.map(t => {
                            const total = Object.values(t.feeBreakdown).reduce((a, b) => a + b, 0);
                            return {
                                installmentId: generateUUID(),
                                description: t.description,
                                installmentType: "TERM", // Defaulting to TERM as per template usage
                                startDate: t.startDate,
                                endDate: t.endDate,
                                dueDate: t.dueDate,
                                feeBreakdown: t.feeBreakdown,
                                totalFee: total,
                                lateFee: 0
                            };
                        }),
                        orgId
                    };

                    // Post
                    const postRes = await axios.post(configData.GET_FEE_STRUCTURE, payload, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            'Content-Type': 'application/json',
                            'Accept': 'application/json, application/hal+json'
                        }
                    });

                    const createdFeeStructureId = postRes.data?.feeStructureId || postRes.data?.id;
                    if (createdFeeStructureId) {
                        console.log('Created feeStructureId:', createdFeeStructureId);
                    }

                    stats.success++;

                } catch (err) {
                    console.error(err);
                    stats.failed++;
                    stats.errors.push({
                        row: "N/A",
                        reason: `Failed to create for ${item.className} (${item.academicYear}): ${err.message}`
                    });
                }
            }

            setImportSummary(stats);
            setUploadStatus('complete');
            
            if (stats.success > 0) {
                toast.success(`Successfully created ${stats.success} fee structure(s).`);
            }
            if (stats.failed > 0) {
                toast.error(`Failed to create ${stats.failed} fee structure(s). Check errors below.`);
            }
        };

        fileReader.readAsText(file);
    };

    const generateTemplate = () => {
        if (isSmallOrg) {
            const headers = "AcademicYear,TermName,StartDate,EndDate,DueDate,Fee";
            const row1 = "2025-2026,Term 1,2025-06-01,2025-09-30,2025-06-15,15000";
            const row2 = "2025-2026,Term 2,2025-10-01,2025-12-31,2025-10-15,15000";
            return `${headers}\n${row1}\n${row2}`;
        }
        const headers = "AcademicYear,TermName,StartDate,EndDate,DueDate,TuitionFee,ExamFee,AdmissionFee,SmartClassFee,ComputerFee,SportsFee,LibraryFee";
        const row1 = "2025-2026,Term 1,2025-06-01,2025-09-30,2025-06-15,12000,500,5000,1000,500,200,100";
        const row2 = "2025-2026,Term 2,2025-10-01,2025-12-31,2025-10-15,12000,0,0,1000,500,200,100";
        return `${headers}\n${row1}\n${row2}`;
    };

    return (
        <div className="import-container">
            <div className="import-card">
                <div className="header-section">
                    <button className="back-button" onClick={() => navigate('/finance')}>
                        &larr; Back to Fees
                    </button>
                    <h2 className="title">Bulk Import Fee for Class</h2>
                </div>

                <div className="content-section">
                    {loadingClasses && <p>Loading classes...</p>}

                    {!loadingClasses && (
                        <>
                            <div className="upload-area">
                                <label className="file-input-label">
                                    <input
                                        type="file"
                                        accept=".csv"
                                        onChange={handleFileChange}
                                        className="hidden-input"
                                    />
                                    <span className="upload-button">
                                        📁 Choose CSV File
                                    </span>
                                </label>
                                {file && <p className="file-name">Selected: {file.name}</p>}
                            </div>



                            <button
                                className={`import-button ${uploadStatus === 'processing' ? 'loading' : ''}`}
                                onClick={handleUpload}
                                disabled={!file || uploadStatus === 'processing'}
                            >
                                {uploadStatus === 'processing' ? 'Processing...' : 'Start Import'}
                            </button>
                        </>
                    )}

                    {uploadStatus === 'complete' && importSummary && (
                        <div className="summary-section">
                            <div className="summary-card">
                                <h3>Import Summary</h3>
                                <div className="summary-stats">
                                    <div className="stat-item success">
                                        <span className="count">{importSummary.success}</span>
                                        <span className="label">Structure Created</span>
                                    </div>
                                    <div className="stat-item warning">
                                        <span className="count">{importSummary.duplicates}</span>
                                        <span className="label">Skipped (Exist)</span>
                                    </div>
                                    <div className="stat-item error">
                                        <span className="count">{importSummary.failed}</span>
                                        <span className="label">Failed</span>
                                    </div>
                                </div>

                                {importSummary.errors.length > 0 && (
                                    <div className="error-details">
                                        <h4>Error Details:</h4>
                                        <ul>
                                            {importSummary.errors.map((err, idx) => (
                                                <li key={idx}>
                                                    <strong>{err.row !== "N/A" ? `Row ${err.row}` : "General"}:</strong> {err.reason}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                <div className="csv-instructions">
                    <h3>CSV Format Requirements</h3>
                    <p>
                        Download
                        <a
                            href={`data:text/csv;charset=utf-8,${encodeURIComponent(generateTemplate())}`}
                            download="FeeTemplate.csv"
                        >
                            sample CSV template
                        </a>
                    </p>
                    <p style={{ marginTop: '10px', fontSize: '12px' }}>
                        <strong>Note:</strong> Rows are grouped by "AcademicYear".
                        Importing for the selected class only.
                        Dates must be YYYY-MM-DD.
                    </p>
                </div>
            </div>
        </div>
    );
}

export default FeeBulkImport;
